import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CreateDepartmentInputSchema, type Department } from '@magimp/shared';
import {
  useDepartmentsList,
  useCreateDepartment,
  usePatchDepartment,
  useArchiveDepartment,
} from '@magimp/shared-hooks';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Input,
  EditorModal,
  ConfirmModal,
  EmptyState,
  ErrorState,
  LoadingState,
  Menu,
  MenuItem,
  IconButton,
  StatusBadge,
  RowCard,
  ToolbarActions,
  useEditorGuard,
  useNarrow,
} from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';

type ShowMode = 'active' | 'all';

export function DepartmentsListPage() {
  const { t } = useTranslation(['departments', 'common', 'users']);
  const narrow = useNarrow();
  const [show, setShow] = useState<ShowMode>('active');
  const [createOpen, setCreateOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<Department | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<Department | null>(null);
  const [unarchiveTarget, setUnarchiveTarget] = useState<Department | null>(null);
  const navigate = useNavigate();

  const filter = show === 'active' ? { active: true } : {};
  const q = useDepartmentsList(filter);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0, flex: 1 }}>
      <header style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, flex: 1 }}>
          {t('departments:title')}
        </h1>
        <ToolbarActions>
          <Button kind="ghost" onClick={() => setShow((m) => (m === 'active' ? 'all' : 'active'))}>
            {show === 'active' ? t('common:actions.showArchive') : t('common:actions.activeOnly')}
          </Button>
          <Button icon="plus" kind="primary" onClick={() => setCreateOpen(true)}>
            {t('departments:actions.create')}
          </Button>
        </ToolbarActions>
      </header>

      {q.error && (
        <ErrorState
          title={t('common:states.loadError')}
          text={errorMessage(q.error)}
          onRetry={() => void q.refetch()}
        />
      )}
      {q.isPending && <LoadingState />}
      {q.isSuccess && q.data.length === 0 && (
        <EmptyState title={t('common:states.emptyTitle')} text={t('departments:empty')} />
      )}
      {q.isSuccess &&
        q.data.length > 0 &&
        (narrow ? (
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {q.data.map((d) => (
              <li key={d.id} style={{ listStyle: 'none' }}>
                <RowCard
                  onSelect={() => navigate(`/departments/${d.id}`)}
                  ariaLabel={d.name}
                  archived={!!d.archivedAt}
                  header={
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg-1)' }}>
                      {d.name}
                    </div>
                  }
                  actions={
                    <>
                      <StatusBadge status={d.archivedAt ? 'archive' : 'active'} />
                      <DepartmentRowMenu
                        d={d}
                        label={t('users:tabs.actions')}
                        renameLabel={t('common:actions.rename')}
                        archiveLabel={t('common:actions.archive')}
                        unarchiveLabel={t('departments:actions.unarchive')}
                        onRename={() => setRenameTarget(d)}
                        onArchive={() => setArchiveTarget(d)}
                        onUnarchive={() => setUnarchiveTarget(d)}
                      />
                    </>
                  }
                />
              </li>
            ))}
          </ul>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--fg-2)', fontSize: 12 }}>
                <th style={{ padding: '8px 6px' }}>{t('departments:columns.name')}</th>
                <th style={{ padding: '8px 6px' }}>{t('departments:columns.status')}</th>
                <th style={{ padding: '8px 6px', width: 48 }} />
              </tr>
            </thead>
            <tbody>
              {q.data.map((d) => (
                <tr
                  key={d.id}
                  style={{ borderTop: '1px solid var(--border-2)', cursor: 'pointer' }}
                  onClick={() => navigate(`/departments/${d.id}`)}
                >
                  <td style={{ padding: '10px 6px' }}>{d.name}</td>
                  <td style={{ padding: '10px 6px' }}>
                    {d.archivedAt ? (
                      <StatusBadge status="archive" />
                    ) : (
                      <StatusBadge status="active" />
                    )}
                  </td>
                  <td
                    style={{ padding: '10px 6px', textAlign: 'right' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DepartmentRowMenu
                      d={d}
                      label={t('users:tabs.actions')}
                      renameLabel={t('common:actions.rename')}
                      archiveLabel={t('common:actions.archive')}
                      unarchiveLabel={t('departments:actions.unarchive')}
                      onRename={() => setRenameTarget(d)}
                      onArchive={() => setArchiveTarget(d)}
                      onUnarchive={() => setUnarchiveTarget(d)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}

      {createOpen && <DepartmentCreateModal onClose={() => setCreateOpen(false)} />}
      {renameTarget && (
        <DepartmentRenameModal dept={renameTarget} onClose={() => setRenameTarget(null)} />
      )}
      {archiveTarget && (
        <DepartmentArchiveConfirm
          dept={archiveTarget}
          archived
          onClose={() => setArchiveTarget(null)}
        />
      )}
      {unarchiveTarget && (
        <DepartmentArchiveConfirm
          dept={unarchiveTarget}
          archived={false}
          onClose={() => setUnarchiveTarget(null)}
        />
      )}
    </div>
  );
}

function DepartmentCreateModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation(['departments', 'common']);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<{ name: string }>({
    resolver: zodResolver(CreateDepartmentInputSchema),
    defaultValues: { name: '' },
  });
  const create = useCreateDepartment();
  const showToast = useToastStore((s) => s.show);
  const onSubmit = (values: { name: string }) => {
    create.mutate(values, {
      onSuccess: () => {
        showToast({ tone: 'success', text: t('departments:toasts.created') });
        // Reset baseline so guard sees clean state before closing.
        reset({ name: '' });
        onClose();
      },
      onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
    });
  };
  return (
    <EditorModal
      open={true}
      title={t('departments:modals.createTitle')}
      width={420}
      dirty={isDirty}
      busy={create.isPending}
      onConfirmClose={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <Input
              value={field.value}
              onChange={field.onChange}
              invalid={!!errors.name}
              placeholder={t('departments:fields.namePlaceholder')}
            />
          )}
        />
        {errors.name?.message && (
          <span role="alert" style={{ fontSize: 11, color: 'var(--status-error-fg)' }}>
            {errors.name.message}
          </span>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <CancelButton onClose={onClose} label={t('common:actions.cancel')} />
          <Button type="submit" kind="primary" disabled={create.isPending}>
            {t('common:actions.create')}
          </Button>
        </div>
      </form>
    </EditorModal>
  );
}

function DepartmentRenameModal({ dept, onClose }: { dept: Department; onClose: () => void }) {
  const { t } = useTranslation(['departments', 'common']);
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<{ name: string }>({
    resolver: zodResolver(CreateDepartmentInputSchema),
    defaultValues: { name: dept.name },
  });
  // WHY: Dirty is computed as trimmed comparison vs baseline so that reverting
  // to the original name is treated as clean (Decision 3 — revert-to-baseline).
  // formState.isDirty uses initial RHF comparison which is string-exact, but
  // the submit path uses trim; keep them consistent by mirroring trim here.
  const currentName = watch('name');
  const dirty = currentName.trim() !== dept.name.trim();

  const patch = usePatchDepartment(dept.id);
  const showToast = useToastStore((s) => s.show);
  const onSubmit = (values: { name: string }) => {
    if (values.name.trim() === dept.name.trim()) {
      onClose();
      return;
    }
    patch.mutate(values, {
      onSuccess: () => {
        showToast({ tone: 'success', text: t('departments:toasts.renamed') });
        // Reset baseline to the new name so guard sees clean state before closing.
        reset({ name: values.name });
        onClose();
      },
      onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
    });
  };
  return (
    <EditorModal
      open={true}
      title={t('departments:modals.renameTitle')}
      width={420}
      dirty={dirty}
      busy={patch.isPending}
      onConfirmClose={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <Input
              value={field.value}
              onChange={field.onChange}
              invalid={!!errors.name}
              placeholder={t('departments:fields.namePlaceholder')}
            />
          )}
        />
        {errors.name?.message && (
          <span role="alert" style={{ fontSize: 11, color: 'var(--status-error-fg)' }}>
            {errors.name.message}
          </span>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <CancelButton onClose={onClose} label={t('common:actions.cancel')} />
          <Button type="submit" kind="primary" disabled={patch.isPending}>
            {t('common:actions.save')}
          </Button>
        </div>
      </form>
    </EditorModal>
  );
}

// WHY: Cancel is an in-form close affordance, not a Modal-chrome close path
// (X / Esc / backdrop) — those are handled by EditorModal internally. Route
// Cancel through `confirmDestructive` so a dirty form still prompts before
// discarding typed input.
function CancelButton({ onClose, label }: { onClose: () => void; label: string }) {
  const { confirmDestructive } = useEditorGuard();
  return (
    <Button kind="ghost" onClick={() => confirmDestructive(onClose)}>
      {label}
    </Button>
  );
}

function DepartmentArchiveConfirm({
  dept,
  archived,
  onClose,
}: {
  dept: Department;
  archived: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation(['departments', 'common']);
  // ConfirmModal calls `onClose` synchronously immediately after `onConfirm`,
  // unmounting this component before the mutation's per-call onSuccess can
  // fire (TanStack Query v5 drops per-call callbacks once the host unmounts).
  // Mirror the UserDeactivateConfirm visible/useEffect pattern so the host
  // stays mounted past the modal close until the mutation settles.
  const archive = useArchiveDepartment(dept.id);
  const showToast = useToastStore((s) => s.show);
  const [visible, setVisible] = useState(true);
  const confirmingRef = useRef(false);
  useEffect(() => {
    if (visible) return;
    if (confirmingRef.current) {
      if (archive.isPending) return;
      if (archive.isError) {
        confirmingRef.current = false;
        setVisible(true);
        return;
      }
      onClose();
      return;
    }
    onClose();
  }, [visible, archive.isPending, archive.isError, onClose]);

  if (!visible) return null;

  const onConfirm = () => {
    confirmingRef.current = true;
    archive.mutate(
      { archived },
      {
        onSuccess: () => {
          showToast({
            tone: 'success',
            text: archived ? t('departments:toasts.archived') : t('departments:toasts.restored'),
          });
        },
        onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
      },
    );
  };
  return (
    <ConfirmModal
      title={
        archived ? t('departments:confirm.archiveTitle') : t('departments:confirm.restoreTitle')
      }
      text={
        archived
          ? t('departments:confirm.archiveText', { name: dept.name })
          : t('departments:confirm.restoreText', { name: dept.name })
      }
      confirmLabel={archived ? t('common:actions.archive') : t('departments:actions.unarchive')}
      cancelLabel={t('common:actions.cancel')}
      danger={archived}
      onConfirm={onConfirm}
      onClose={() => setVisible(false)}
    />
  );
}

interface DepartmentRowMenuProps {
  d: Department;
  label: string;
  renameLabel: string;
  archiveLabel: string;
  unarchiveLabel: string;
  onRename: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
}

function DepartmentRowMenu({
  d,
  label,
  renameLabel,
  archiveLabel,
  unarchiveLabel,
  onRename,
  onArchive,
  onUnarchive,
}: DepartmentRowMenuProps) {
  return (
    <Menu
      trigger={(triggerProps) => (
        <IconButton
          ref={triggerProps.ref as (el: HTMLButtonElement | null) => void}
          label={label}
          icon="more-vertical"
          aria-haspopup={triggerProps['aria-haspopup']}
          aria-expanded={triggerProps['aria-expanded']}
          aria-controls={triggerProps['aria-controls']}
          onClick={triggerProps.onClick}
          onKeyDown={triggerProps.onKeyDown}
        />
      )}
    >
      <MenuItem onClick={onRename}>{renameLabel}</MenuItem>
      {d.archivedAt ? (
        <MenuItem onClick={onUnarchive}>{unarchiveLabel}</MenuItem>
      ) : (
        <MenuItem onClick={onArchive}>{archiveLabel}</MenuItem>
      )}
    </Menu>
  );
}
